import React, { useEffect } from 'react';
import { useSocialMediaConfig } from '../../hooks/useSocialMediaConfig';

export interface SocialMediaPlatform {
  id: 'facebook' | 'twitter' | 'instagram';
  name: string;
  icon: string;
  configured: boolean;
}

export interface SocialMediaCheckboxesProps {
  selectedPlatforms: string[];
  onSelectionChange: (platforms: string[]) => void;
  disabled?: boolean;
  className?: string;
}

export const SocialMediaCheckboxes: React.FC<SocialMediaCheckboxesProps> = ({
  selectedPlatforms,
  onSelectionChange,
  disabled = false,
  className = '',
}) => {
  const { config, loading: configLoading } = useSocialMediaConfig();

  // Create platforms array from configuration
  const platforms: SocialMediaPlatform[] = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: '📘',
      configured: config?.platforms.facebook.enabled && config?.platforms.facebook.hasCredentials || false,
    },
    {
      id: 'twitter',
      name: 'X (Twitter)',
      icon: '🐦',
      configured: config?.platforms.twitter.enabled && config?.platforms.twitter.hasCredentials || false,
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: '📷',
      configured: config?.platforms.instagram.enabled && config?.platforms.instagram.hasCredentials || false,
    },
  ];

  // Automatically filter out unconfigured platforms from selection
  useEffect(() => {
    if (!config || configLoading) return;

    const configuredPlatformIds = platforms
      .filter(platform => platform.configured)
      .map(platform => platform.id);

    const filteredSelection = selectedPlatforms.filter(platformId => 
      configuredPlatformIds.includes(platformId as 'facebook' | 'twitter' | 'instagram')
    );

    // Only update if the selection has changed
    if (filteredSelection.length !== selectedPlatforms.length || 
        !filteredSelection.every(id => selectedPlatforms.includes(id))) {
      onSelectionChange(filteredSelection);
    }
  }, [config, configLoading, platforms, selectedPlatforms, onSelectionChange]);

  const handlePlatformToggle = (platformId: string) => {
    if (isDisabled) return;

    // Find the platform to check if it's configured
    const platform = platforms.find(p => p.id === platformId);
    if (!platform?.configured) return; // Don't allow toggling unconfigured platforms

    const isSelected = selectedPlatforms.includes(platformId);
    let newSelection: string[];

    if (isSelected) {
      // Remove platform from selection
      newSelection = selectedPlatforms.filter(id => id !== platformId);
    } else {
      // Add platform to selection
      newSelection = [...selectedPlatforms, platformId];
    }

    onSelectionChange(newSelection);
  };

  const handleSelectAll = () => {
    if (isDisabled) return;
    
    const configuredPlatforms = platforms
      .filter(platform => platform.configured)
      .map(platform => platform.id);
    
    onSelectionChange(configuredPlatforms);
  };

  const handleSelectNone = () => {
    if (isDisabled) return;
    onSelectionChange([]);
  };

  const configuredPlatforms = platforms.filter(platform => platform.configured);
  const allConfiguredSelected = configuredPlatforms.every(platform => 
    selectedPlatforms.includes(platform.id)
  );
  const noneSelected = selectedPlatforms.length === 0;
  const isDisabled = disabled || configLoading;

  if (configLoading) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-urban">
            Share on Social Media
          </label>
        </div>
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-urban-50">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-urban-200 rounded"></div>
                <div className="w-20 h-4 bg-urban-200 rounded"></div>
              </div>
              <div className="w-16 h-4 bg-urban-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header with bulk actions */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-urban">
          Share on Social Media
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={isDisabled || allConfiguredSelected}
            className="text-xs text-cardboard hover:text-cardboard-600 disabled:text-urban-300 disabled:cursor-not-allowed"
          >
            Select All
          </button>
          <span className="text-xs text-urban-300">|</span>
          <button
            type="button"
            onClick={handleSelectNone}
            disabled={isDisabled || noneSelected}
            className="text-xs text-cardboard hover:text-cardboard-600 disabled:text-urban-300 disabled:cursor-not-allowed"
          >
            Select None
          </button>
        </div>
      </div>

      {/* Platform checkboxes */}
      <div className="space-y-2">
        {platforms.map((platform) => {
          const isSelected = selectedPlatforms.includes(platform.id);
          const isPlatformDisabled = isDisabled || !platform.configured;

          return (
            <div
              key={platform.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                isPlatformDisabled
                  ? 'bg-urban-50 border-urban-200 cursor-not-allowed'
                  : isSelected
                  ? 'bg-verge-50 border-verge-300 hover:bg-verge-100'
                  : 'bg-white border-urban-200 hover:border-urban-300 cursor-pointer'
              }`}
              onClick={() => !isPlatformDisabled && handlePlatformToggle(platform.id)}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`platform-${platform.id}`}
                    checked={isSelected}
                    onChange={() => handlePlatformToggle(platform.id)}
                    disabled={isPlatformDisabled}
                    className="h-4 w-4 text-cardboard focus:ring-cardboard border-urban-300 rounded disabled:cursor-not-allowed"
                    aria-label={`Share on ${platform.name}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{platform.icon}</span>
                  <span className={`font-medium ${isPlatformDisabled ? 'text-urban-400' : 'text-urban'}`}>
                    {platform.name}
                  </span>
                </div>
              </div>

              {/* Configuration status indicator */}
              <div className="flex items-center gap-2">
                {platform.configured ? (
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-verge rounded-full"></div>
                    <span className="text-xs text-verge font-medium">Configured</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-rust rounded-full"></div>
                    <span className="text-xs text-rust font-medium">Not Configured</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Help text */}
      <div className="text-xs text-urban-300 space-y-1">
        <p>Selected platforms will automatically share this report when approved.</p>
        {configuredPlatforms.length < platforms.length && (
          <p className="text-rust">
            Some platforms are not configured and cannot be used for sharing.
          </p>
        )}
        {selectedPlatforms.length === 0 && (
          <p className="text-treasure font-medium">
            No platforms selected - report will be approved without social media sharing.
          </p>
        )}
      </div>
    </div>
  );
};